/**
 * Fog-of-war vision math (feat/fog-of-war).
 *
 * Pure + dependency-free: it takes a `sampleHeight(x, z)` callback instead of
 * importing the heightmap, so the SAME code runs on both sides —
 *   - server: drives per-client `StateView` entity culling (who is hidden), and
 *   - client: drives the fog texture (what terrain is dark).
 * Running identical logic is the whole point: authoritative hiding and the fog
 * visual can never disagree (an enemy can't appear in a tile the client drew as
 * fogged). See the `project_fow_precedes_b5` memory + `.lavish/fow-research.html`
 * for the research and decisions (Option B active vision; SC2 high-ground
 * occlusion; snap-invisible).
 *
 * Model (top-down radius vision + SC2 high-ground occlusion):
 *   A point is visible to a team if SOME living team unit is within its sight
 *   radius AND has line-of-sight, where line-of-sight means:
 *     1. the target is not on meaningfully HIGHER ground than the observer
 *        (SC2: "low ground can't see up the ramp"), and
 *     2. no terrain crest between observer and target rises above the
 *        observer's eye-level sightline (a hill/cliff occludes what's behind it).
 *   "High ground sees low" falls out for free: a higher observer's eye-level
 *   line clears all the lower terrain in front of it.
 *
 * All numbers here are pure tuning (KTS/SC2-derived) — adjust freely.
 */

export type SampleHeight = (x: number, z: number) => number;

export interface VisionSource {
  x: number;
  z: number;
  /** Effective sight radius in world units (already night-scaled by caller). */
  radius: number;
}

// --- Tunables -------------------------------------------------------------
/** Daytime sight radius (world units). 22u > autoAcquire SIGHT (12u). */
export const SIGHT_RADIUS_DAY = 22;
/** Night sight radius — modest shrink (−4u detection range, NOT a visual dark-wall;
 *  lighting readability is handled separately). Night Eye (Foraging tier 5) negates it. */
export const SIGHT_RADIUS_NIGHT = 18;
/** Larger radius at which smoke / campfires are detectable (Foraging "spot smoke"). */
export const SMOKE_DETECT_RADIUS = 30;

/**
 * A target more than this many height units ABOVE the observer is on "high
 * ground" and cannot be seen (SC2 rule). Sized so gentle rolling slopes don't
 * fog units, but a real ramp/cliff level break (≥~2u on the KTS heightmap)
 * blocks up-vision. The heightmap spans roughly -4.6..23.4.
 */
const UP_TOLERANCE = 2.0;
/** Observer eye height above terrain — the flat sightline used for occlusion. */
const EYE_HEIGHT = 1.5;
/** Terrain must exceed the sightline by more than this to occlude (noise guard). */
const OCCLUSION_MARGIN = 0.6;
/** Raymarch step along observer→target (world units). Finer = costlier + sharper. */
const LOS_STEP = 0.75;

/** Clamp helper. */
function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Night-scaled sight radius. `nightness` 0 = full day, 1 = full night.
 * `hasNightEye` negates the night penalty entirely (Foraging L5 capstone).
 */
export function sightRadius(nightness: number, hasNightEye = false): number {
  if (hasNightEye) return SIGHT_RADIUS_DAY;
  return SIGHT_RADIUS_DAY + (SIGHT_RADIUS_NIGHT - SIGHT_RADIUS_DAY) * clamp01(nightness);
}

/**
 * SC2-style line-of-sight from observer (ox,oz) to target (tx,tz). Returns
 * false if the target is on higher ground (up-vision blocked) or a terrain
 * crest occludes it. See module header for the model.
 */
export function hasLineOfSight(
  ox: number,
  oz: number,
  tx: number,
  tz: number,
  sampleHeight: SampleHeight,
): boolean {
  const oh = sampleHeight(ox, oz);
  const th = sampleHeight(tx, tz);
  // (1) Can't see meaningfully-higher ground.
  if (th > oh + UP_TOLERANCE) return false;

  const ddx = tx - ox;
  const ddz = tz - oz;
  const dist = Math.sqrt(ddx * ddx + ddz * ddz);
  if (dist < LOS_STEP) return true;

  // (2) March the segment; any crest above the observer's eye-level sightline
  // occludes. Using the FLAT observer eye-line (not a line aimed at the target)
  // is what makes "high sees low / low can't see over a hill" feel SC2-correct.
  const sightH = oh + EYE_HEIGHT;
  const ux = ddx / dist;
  const uz = ddz / dist;
  for (let d = LOS_STEP; d < dist; d += LOS_STEP) {
    const terrH = sampleHeight(ox + ux * d, oz + uz * d);
    if (terrH > sightH + OCCLUSION_MARGIN) return false;
  }
  return true;
}

/**
 * True if ANY source can see (radius + LoS) the point (tx, tz). The primary
 * server-side gate: call once per candidate entity against the team's sources.
 */
export function canSee(
  sources: readonly VisionSource[],
  tx: number,
  tz: number,
  sampleHeight: SampleHeight,
): boolean {
  for (let i = 0; i < sources.length; i++) {
    const s = sources[i]!;
    const dx = tx - s.x;
    const dz = tz - s.z;
    if (dx * dx + dz * dz > s.radius * s.radius) continue;
    if (hasLineOfSight(s.x, s.z, tx, tz, sampleHeight)) return true;
  }
  return false;
}

// --- Client fog grid ------------------------------------------------------

export interface FogGrid {
  /** Number of cells across X and Z. */
  cols: number;
  rows: number;
  /** World units per cell. */
  cellSize: number;
  /** World coord of the (0,0) cell's lower corner. Map is 0..255 → origin 0. */
  originX: number;
  originZ: number;
}

/**
 * Index of cell (cx, cz) in a row-major grid. */
export function cellIndex(grid: FogGrid, cx: number, cz: number): number {
  return cz * grid.cols + cx;
}

/**
 * Write 1 into `out` for every cell whose CENTER is currently visible to
 * `sources`, 0 otherwise (the "currently visible" layer). Only cells within a
 * source's radius bounding box are tested — distant cells stay 0 cheaply (the
 * RTS "only process cells near a vision source" optimization). The caller ORs
 * this into a persistent "explored" mask to get the three fog states.
 *
 * `out` must be length `grid.cols * grid.rows`; it is fully zeroed first.
 */
export function computeVisibleCells(
  sources: readonly VisionSource[],
  sampleHeight: SampleHeight,
  grid: FogGrid,
  out: Uint8Array,
): void {
  out.fill(0);
  const { cols, rows, cellSize, originX, originZ } = grid;
  for (let i = 0; i < sources.length; i++) {
    const s = sources[i]!;
    // Cell bounding box for this source's radius.
    const minCx = Math.max(0, Math.floor((s.x - s.radius - originX) / cellSize));
    const maxCx = Math.min(cols - 1, Math.floor((s.x + s.radius - originX) / cellSize));
    const minCz = Math.max(0, Math.floor((s.z - s.radius - originZ) / cellSize));
    const maxCz = Math.min(rows - 1, Math.floor((s.z + s.radius - originZ) / cellSize));
    for (let cz = minCz; cz <= maxCz; cz++) {
      for (let cx = minCx; cx <= maxCx; cx++) {
        const idx = cz * cols + cx;
        if (out[idx]) continue; // already visible via another source
        const wx = originX + (cx + 0.5) * cellSize;
        const wz = originZ + (cz + 0.5) * cellSize;
        // canSee re-checks all sources, but the early radius reject keeps it cheap.
        if (canSee(sources, wx, wz, sampleHeight)) out[idx] = 1;
      }
    }
  }
}
